import { useState } from "react";

import { t, type Language } from "../lib/i18n";

interface CorrelationIdProps {
  /** Full drill-down id. The chip shows a truncated form; copy yields the
   * full value. */
  value: string;
  language: Language;
  /** Visible truncation length in chars. Default 12 mirrors the prior
   * truncated mono span this component replaces. */
  truncate?: number;
}

/** Copy-on-click reference chip (F37 §12.3/§12.4). Replaces the old
 * SR-noisy truncated `<span>`: it is now a real `<button>` labelled
 * "Copy reference {id}", and an `aria-live` region announces
 * "Reference copied" on success. The visible glyph is the truncated id
 * in a mono pill — the persona rarely needs it, but when they do (to
 * paste into the invoice-search box) it copies the FULL id in one click.
 *
 * Universal: no tenant-specific copy; reused across the activity,
 * exceptions, and approvals feeds. */
export function CorrelationId({
  value,
  language,
  truncate = 12,
}: CorrelationIdProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const shown = value.length > truncate ? value.slice(0, truncate) : value;

  const onCopy = (): void => {
    const confirm = (): void => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    };
    // `navigator.clipboard` is absent in some embedded webviews and in
    // jsdom — fail soft (the id is still visible to read by eye) rather
    // than throw inside an onClick.
    try {
      const p = navigator.clipboard?.writeText(value);
      if (p) {
        void p.then(confirm, () => undefined);
      }
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onCopy}
        aria-label={t("reference.copy", language)}
        title={t("reference.label", language)}
        data-testid="correlation-copy"
        className="rounded bg-kwanza-surface px-1.5 py-0.5 font-mono text-xs text-kwanza-inkSoft hover:bg-kwanza-accentSoft hover:text-kwanza-accentText"
      >
        {shown}
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? t("reference.copied", language) : ""}
      </span>
    </span>
  );
}
